/**
 * 「系统参数运维」页的共享外壳与跨卡通信。
 *
 * 这里放三类东西：
 * 1. ``Panel`` / ``Stat`` / ``SOURCE_PILL`` —— 各配置卡与运维卡共用的视觉外壳；
 * 2. ``SysConfigBus`` —— 页头「全部刷新」与左侧锚点导航的状态点，都需要跨卡通信：
 *    页头要广播「重新拉一遍」，每张卡要把三件事上报回去供导航与概览徽标统计：
 *    - ``source``（redis / code_default）：状态点实心 / 空心；
 *    - ``failed``：拉取失败。不上报的话，失败项只是从 sources 里消失，
 *      概览徽标就变成「只给分子不给分母」——用户分不清 5/7 还是 7/7；
 *    - ``dirty``：有未保存改动。卡片自己知道脏，但用户滚到第 6 张卡时
 *      看不见第 3 张卡还没保存，必须把这件事冒泡到常驻的导航 / 页头上。
 *    用一个极轻的 context 做，不引入状态库。
 * 3. ``useConfigResource`` —— 「拉取 + loading + 失败重试 + 订阅全局刷新 + 上报状态」
 *    这段在 7 张卡里一模一样，抽成 hook，避免又抄七遍。
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode,
} from 'react';
import { App, Button, Popover, Spin, Tooltip } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { SystemConfigSource } from '@apis/simulation';

/** 各配置项 VO 的公共形状（三元组：当前值 / 来源 / 代码兜底值）。 */
export interface ConfigVo {
  currentValue: string;
  source: SystemConfigSource;
  defaultValue: string;
}

export const SOURCE_PILL: Record<SystemConfigSource, ReactNode> = {
  db: <span className="sc-pill redis"><i className="sc-dot" />数据库配置</span>,
  snapshot: <span className="sc-pill def">默认快照</span>,
  redis: <span className="sc-pill redis"><i className="sc-dot" />Redis 配置</span>,
  code_default: <span className="sc-pill def">代码默认</span>,
};

/* ------------------------------------------------------------------ *
 * 跨卡通信：全局刷新广播 + source 上报
 * ------------------------------------------------------------------ */

/** id → true 的标记表（加载失败 / 有未保存改动都用这个形状） */
type FlagMap = Record<string, true>;

interface SysConfigBus {
  /** 单调递增；每张卡把它放进 fetch 的 effect 依赖里即可响应「全部刷新」 */
  refreshTick: number;
  refreshAll: () => void;
  /** 配置项当前来源表，key 为锚点 id；未加载完成的项不在表里 */
  sources: Record<string, SystemConfigSource>;
  reportSource: (id: string, source: SystemConfigSource | null) => void;
  /** 拉取失败的配置项。概览徽标要能说清「7 项里有 2 项没拉到」 */
  failedIds: FlagMap;
  reportFailed: (id: string, failed: boolean) => void;
  /** 有未保存改动的配置项。用于导航状态点与页头的「N 项未保存」提示 */
  dirtyIds: FlagMap;
  reportDirty: (id: string, dirty: boolean) => void;
}

const BusContext = createContext<SysConfigBus | null>(null);

/**
 * 一张 id → true 的标记表 + 幂等 setter。
 * 「同值直接返回 prev」这一条是必须的：setter 会被放进各卡的 effect 依赖里，
 * 每次都产生新对象的话 provider 重渲染 → 各卡 effect 重跑 → 无限抖动。
 */
function useFlagMap(): [FlagMap, (id: string, on: boolean) => void] {
  const [map, setMap] = useState<FlagMap>({});
  const report = useCallback((id: string, on: boolean) => {
    setMap((prev) => {
      if (on === (id in prev)) return prev;
      if (!on) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: true };
    });
  }, []);
  return [map, report];
}

export function SysConfigBusProvider({ children }: { children: ReactNode }) {
  const [refreshTick, setRefreshTick] = useState(0);
  const [sources, setSources] = useState<Record<string, SystemConfigSource>>({});
  const [failedIds, reportFailed] = useFlagMap();
  const [dirtyIds, reportDirty] = useFlagMap();

  const refreshAll = useCallback(() => setRefreshTick((t) => t + 1), []);

  const reportSource = useCallback((id: string, source: SystemConfigSource | null) => {
    setSources((prev) => {
      if (source == null) {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      if (prev[id] === source) return prev; // 同值不触发重渲染，防 effect 抖动
      return { ...prev, [id]: source };
    });
  }, []);

  const value = useMemo<SysConfigBus>(
    () => ({
      refreshTick, refreshAll, sources, reportSource,
      failedIds, reportFailed, dirtyIds, reportDirty,
    }),
    [
      refreshTick, refreshAll, sources, reportSource,
      failedIds, reportFailed, dirtyIds, reportDirty,
    ],
  );

  return <BusContext.Provider value={value}>{children}</BusContext.Provider>;
}

export function useSysConfigBus(): SysConfigBus {
  const ctx = useContext(BusContext);
  if (!ctx) throw new Error('useSysConfigBus 必须在 SysConfigBusProvider 内使用');
  return ctx;
}

/**
 * 把「本卡有未保存改动」上报给总线。
 *
 * 各卡的 ``isDirty`` 原本只用来 disable 保存按钮——用户在第 3 张卡改了没保存、
 * 滚到第 6 张，全页任何位置都看不出还有改动。上报后由常驻的左侧导航状态点
 * 与页头徽标统一呈现。卸载时撤回，避免卡片消失了标记还挂着。
 */
export function useReportDirty(id: string, dirty: boolean): void {
  const { reportDirty } = useSysConfigBus();
  useEffect(() => {
    reportDirty(id, dirty);
  }, [id, dirty, reportDirty]);
  useEffect(() => () => reportDirty(id, false), [id, reportDirty]);
}

/* ------------------------------------------------------------------ *
 * 配置拉取 hook
 * ------------------------------------------------------------------ */

interface UseConfigResourceOptions<T extends ConfigVo> {
  /** 锚点 id，同时用作 source 上报的 key */
  id: string;
  /** 加载失败提示里的配置项名字 */
  title: string;
  fetcher: () => Promise<T>;
  /** 拉到新 VO 后写各自 draft 的钩子；内部用 ref 保存，不必 useCallback 包 */
  onLoaded: (cfg: T) => void;
}

/**
 * 「Redis 优先 + 代码兜底」配置项的拉取骨架：首帧自动拉、响应页头「全部刷新」、
 * 把 source 上报给导航。返回的 ``applyConfig`` 供保存 / 重置后同步本地状态。
 */
export function useConfigResource<T extends ConfigVo>({
  id, title, fetcher, onLoaded,
}: UseConfigResourceOptions<T>) {
  const { message } = App.useApp();
  const { refreshTick, reportSource, reportFailed } = useSysConfigBus();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<T | null>(null);

  // onLoaded / fetcher 多半是内联箭头函数，放进依赖会每帧重建 → 无限拉取。
  // 用 ref 存最新引用，effect 只依赖真正会变的 refreshTick。
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const applyConfig = useCallback((cfg: T) => {
    setConfig(cfg);
    onLoadedRef.current(cfg);
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      applyConfig(await fetcherRef.current());
      reportFailed(id, false); // 重试成功要把失败标记撤下来，否则徽标永远挂着
    } catch (err) {
      // 失败不仅要弹 message（会自己消失），还要上报——否则这一项只是静静地
      // 从 sources 里缺席，页头徽标看不出「少的那两项是没拉到而不是不存在」
      reportFailed(id, true);
      message.error(`加载 ${title} 失败`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [applyConfig, id, message, reportFailed, title]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig, refreshTick]);

  // 上报 source 供左侧导航状态点与页头概览统计；卸载时撤回
  useEffect(() => {
    reportSource(id, config?.source ?? null);
  }, [id, config?.source, reportSource]);
  useEffect(() => () => {
    reportSource(id, null);
    reportFailed(id, false);
  }, [id, reportSource, reportFailed]);

  return { loading, config, applyConfig, fetchConfig };
}

/* ------------------------------------------------------------------ *
 * 视觉外壳
 * ------------------------------------------------------------------ */

/**
 * 入场 stagger 步长（ms）。旧值 45ms × 9 + 0.42s 动画 ≈ 0.83s 才铺完全页，
 * 刷新一次要盯着卡片一张张飘进来将近一秒。压到 24ms × 9 + 0.3s ≈ 0.54s。
 * 与 index.less 里 `.sc-card` / `.sc-row` 的动画时长是一对，改这里记得同步那边注释。
 */
export const STAGGER_MS = 24;

export interface PanelProps {
  variant: 'cfg' | 'ops';
  kicker: string;
  title: string;
  /** 一句话说明，直接显示在标题下（短说明不该藏进气泡里） */
  subtitle?: ReactNode;
  /** 长说明（结构 / 约束），收进 info 气泡 */
  info?: ReactNode;
  tools?: ReactNode;
  /** 载入 stagger 序号，全页唯一递增 */
  index: number;
  /** 锚点 id，左侧导航据此 scrollIntoView */
  id?: string;
  /** 有未保存改动：左色条转琥珀加粗 + 卡头挂「未保存」标记 */
  dirty?: boolean;
  children: ReactNode;
}

/** 通用面板外壳：左色条 + kicker/title + 一句话副标题 + 说明气泡 + 右侧工具位。 */
export function Panel({
  variant, kicker, title, subtitle, info, tools, index, id, dirty, children,
}: PanelProps) {
  return (
    <div
      id={id}
      className={`sc-card ${variant}${dirty ? ' dirty' : ''}`}
      style={{ animationDelay: `${index * STAGGER_MS}ms` } as CSSProperties}
    >
      <div className="sc-head">
        <div className="sc-head-main">
          <div className="sc-kicker">{kicker}</div>
          <div className="sc-title">{title}</div>
          {subtitle != null && <div className="sc-subtitle">{subtitle}</div>}
        </div>
        <div className="sc-head-tools">
          {dirty && <span className="sc-dirty-tag">未保存</span>}
          {tools}
          {info != null && (
            <Popover
              trigger="click"
              placement="bottomRight"
              title="说明"
              content={<div className="sc-info-pop">{info}</div>}
            >
              <Button type="text" size="small" icon={<InfoCircleOutlined />} />
            </Popover>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

export function Stat({
  label, value, tone,
}: { label: string; value: ReactNode; tone?: 'ok' | 'warn' | 'bad' }) {
  return (
    <span className="sc-stat">
      {label}
      <b className={tone}>{value}</b>
    </span>
  );
}

/** 各卡右上角统一的「重新拉取」图标按钮。 */
export function RefreshButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <Tooltip title="重新拉取最新配置">
      <Button type="text" size="small" icon={<ReloadOutlined />} onClick={onClick} loading={loading} />
    </Tooltip>
  );
}

/** 首屏 loading 占位。 */
export function PanelLoading() {
  return <div className="sc-loading"><Spin size="small" /></div>;
}

/** 拉取失败占位（带重试）。 */
export function PanelError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="sc-error">
      加载配置失败
      <Button type="link" size="small" icon={<ReloadOutlined />} onClick={onRetry}>重试</Button>
    </div>
  );
}

/** 默认值缩略展示（超 60 字符截断），与原实现一致。 */
export function truncateDefault(value: string, max = 60): string {
  if (!value) return '(空)';
  return value.length > max ? `${value.slice(0, max)}…` : value;
}
